import { DEFAULT_OFFER_NAME } from "../../utils/constant";
import { IntlShape } from "react-intl";
import { formatPrice } from "../../utils/common";
import { OffersInfo } from "../../graphql/onboarding/offer/types/OffersInfo";
import { ConfigOfferInfo } from "../../graphql/onboarding/offer/types/ConfigOfferInfo";
import { ConfigOfferKey, ConfigUnderOfferKey } from "../../enums/offer";
import { getLocale } from "../../services/Locale";
import { UnderOfferInfo } from "../../graphql/onboarding/offer/types/UnderOfferInfo";

export interface OfferChoise {
  offerId?: string | null;
  cgsSapheer: boolean;
  gtLinkcy: boolean;
}
export interface OfferInterface {
  price: string;
  virtualCard: number;
  physicalCard: number;
  payouts: number;
  payoutsInst: number;
  limitUser: number;
  fonctionnalites: Array<string>;
  ddTransactionLimit: string;
  ddMonthlyLimit: string;
  transactionLimit: string;
  transactionDailyLimit: string;
  transactionMonthlyLimit: string;
}
export interface ConfigOptionInterface {
  title: string; // Le titre de l'option
  display: boolean; // Indique si l'option est affichée ou non
}

export interface OptionListInterface {
  [key: string]: ConfigOptionInterface; // Une liste d'options où chaque clé est dynamique (par exemple: "physical", "virtual", etc.)
}

export interface ConfigGroup {
  title: string; // Le titre du groupe de configuration
  display: boolean; // Indique si le groupe est affiché ou non
  optionlist: OptionListInterface; // Les options associées au groupe
}

export interface ConfigOfferInterface {
  businessCard: ConfigGroup; // Groupe pour les cartes d'entreprise
  payout: ConfigGroup; // Groupe pour les virements
  transactions: ConfigGroup; // Groupe pour les limites de transaction
  directDebit: ConfigGroup; // Groupe pour les limites de prélèvements
  users: ConfigGroup; // Groupe pour les utilisateurs
}

export const getPreselectedOffer = (offers: (OffersInfo | null)[] = []) => {
  return (
    offers?.find((offer) => offer?.defaultSelected) ||
    offers?.find((offer) => offer?.name === DEFAULT_OFFER_NAME) ||
    offers[1] ||
    null
  );
};

export const findConfigByKey = (
  config: (ConfigOfferInfo | null | undefined)[],
  key: ConfigOfferKey,
) => config.find((c) => c?.key === key);

export const findDisplayByKey = (
  displays: (UnderOfferInfo | null | undefined)[],
  key: ConfigUnderOfferKey,
) => displays?.find((c) => c?.key === key);

export const getTitleDisplay = ({
  fr,
  en,
  id,
  intl,
}: {
  fr: string | null | undefined;
  en: string | null | undefined;
  intl: IntlShape;
  id: string;
}) => {
  const locale = getLocale();
  return (
    (locale.startsWith("fr")
      ? fr
      : locale.startsWith("en")
      ? en
      : intl.formatMessage({ id })) || ""
  );
};

export const getInformation = (offer: OffersInfo): OfferInterface => {
  const {
    price,
    virtualCard,
    physicalCard,
    payouts,
    payoutsInst,
    limitUser,
    ddTransactionLimit,
    ddMonthlyLimit,
    transactionLimit,
    transactionDailyLimit,
    transactionMonthlyLimit,
  } = offer;

  const formattedPrice =
    price == null ? "" : price <= 0 ? "0" : price.toString();

  const _offer: OfferInterface = {
    price: formattedPrice,
    virtualCard: virtualCard ?? 0,
    physicalCard: physicalCard ?? 0,
    payouts: payouts ?? 0,
    payoutsInst: payoutsInst ?? 0,
    limitUser: limitUser ?? 0,
    ddTransactionLimit: formatCurrency(ddTransactionLimit),
    ddMonthlyLimit: formatCurrency(ddMonthlyLimit),
    transactionLimit: formatCurrency(transactionLimit),
    transactionDailyLimit: formatCurrency(transactionDailyLimit),
    transactionMonthlyLimit: formatCurrency(transactionMonthlyLimit),
    fonctionnalites: [],
  };

  return _offer;
};

export const formatCurrency = (
  value: number | null,
  suffix?: string,
): string => {
  const formattedValue = value != null ? formatPrice(value) : "0 €";
  return suffix ? `${formattedValue} / ${suffix}` : formattedValue;
};

export const getOfferConfig = (
  config: (ConfigOfferInfo | null | undefined)[],
) => {
  const smart_access = findConfigByKey(config, ConfigOfferKey.SMART_ACCESS);
  const payout = findConfigByKey(config, ConfigOfferKey.PAYOUT);
  const limit_transaction = findConfigByKey(
    config,
    ConfigOfferKey.LIMIT_TRANSACTION,
  );
  const limit_direct_debit = findConfigByKey(
    config,
    ConfigOfferKey.LIMIT_DIRECT_DEBIT,
  );
  const business_card = findConfigByKey(config, ConfigOfferKey.BUSINESS_CARD);

  const getDisplays = (
    parent: ConfigOfferInfo | null | undefined,
    keys: ConfigUnderOfferKey[],
  ) =>
    keys.reduce((acc, key) => {
      acc[key] = findDisplayByKey(parent?.displays || [], key);
      return acc;
    }, {} as Record<ConfigUnderOfferKey, any>);

  const businessCardDisplays = getDisplays(business_card, [
    ConfigUnderOfferKey.PHYSICAL_CARD,
    ConfigUnderOfferKey.VIRTUAL_CARD,
    ConfigUnderOfferKey.PAIEMENT_CARD,
  ]);

  const payoutDisplays = getDisplays(payout, [
    ConfigUnderOfferKey.PAYOUT,
    ConfigUnderOfferKey.PAYOUT_INST,
  ]);

  const transactionDisplays = getDisplays(limit_transaction, [
    ConfigUnderOfferKey.LIMIT_TRANSACTION,
    ConfigUnderOfferKey.LIMIT_TRANSACTION_DAILY,
    ConfigUnderOfferKey.LIMIT_TRANSACTION_MONTHLY,
  ]);

  const directDebitDisplays = getDisplays(limit_direct_debit, [
    ConfigUnderOfferKey.LIMIT_DIRECT_DEBIT,
    ConfigUnderOfferKey.LIMIT_DIRECT_DEBIT_MONTHLY,
  ]);

  const smart_accessDisplays = getDisplays(smart_access, [
    ConfigUnderOfferKey.USERS,
  ]);

  return {
    smart_access,
    payout,
    limit_transaction,
    limit_direct_debit,
    business_card,
    ...businessCardDisplays,
    ...payoutDisplays,
    ...transactionDisplays,
    ...directDebitDisplays,
    ...smart_accessDisplays,
  };
};

export const getConfigOffer = (
  config: (ConfigOfferInfo | null | undefined)[],
  intl: IntlShape,
): ConfigOfferInterface => {
  const {
    smart_access,
    payout,
    limit_transaction,
    limit_direct_debit,
    business_card,
    PHYSICAL_CARD: physical,
    VIRTUAL_CARD: virtual,
    PAIEMENT_CARD: cardTransaction,
    LIMIT_TRANSACTION: autorizedTransaction,
    LIMIT_TRANSACTION_DAILY: autorizedTransactionDaily,
    LIMIT_TRANSACTION_MONTHLY: autorizedTransactionMonthly,
    LIMIT_DIRECT_DEBIT: autorizedDirectDebit,
    LIMIT_DIRECT_DEBIT_MONTHLY: autorizedDirectDebitMonthly,
    PAYOUT: normal,
    PAYOUT_INST: inst,
    USERS: users,
  } = getOfferConfig(config);

  const createOption = (key: string, display: UnderOfferInfo | undefined) => ({
    title: getTitleDisplay({
      en: display?.nameEn,
      fr: display?.nameFr,
      id: key,
      intl,
    }),
    display: !!display?.display,
  });

  return {
    businessCard: {
      title: getTitleDisplay({
        en: business_card?.nameEn,
        fr: business_card?.nameFr,
        id: "offer.businessMastercard",
        intl,
      }),
      display: [physical, virtual, cardTransaction].some(
        (item) => item?.display,
      ),
      optionlist: {
        physical: createOption("offer.physical", physical),
        virtual: createOption("offer.virtual", virtual),
        cardTransaction: createOption("offer.cardTransaction", cardTransaction),
      },
    },
    payout: {
      title: getTitleDisplay({
        en: payout?.nameEn,
        fr: payout?.nameFr,
        id: "offer.payouts",
        intl,
      }),
      display: [normal, inst].some((item) => item?.display),
      optionlist: {
        normal: createOption("offer.normal", normal),
        inst: createOption("offer.inst", inst),
      },
    },
    transactions: {
      title: getTitleDisplay({
        en: limit_transaction?.nameEn,
        fr: limit_transaction?.nameFr,
        id: "offer.limitTransactions",
        intl,
      }),
      display: [
        autorizedTransaction,
        autorizedTransactionDaily,
        autorizedTransactionMonthly,
      ].some((item) => item?.display),
      optionlist: {
        autorizedTransaction: createOption(
          "offer.autorized.transaction",
          autorizedTransaction,
        ),
        autorizedTransactionDaily: createOption(
          "offer.autorized.daily",
          autorizedTransactionDaily,
        ),
        autorizedTransactionMonthly: createOption(
          "offer.autorized.monthly",
          autorizedTransactionMonthly,
        ),
      },
    },
    directDebit: {
      title: getTitleDisplay({
        en: limit_direct_debit?.nameEn,
        fr: limit_direct_debit?.nameFr,
        id: "offer.limitDirectDebits",
        intl,
      }),
      display: [autorizedDirectDebit, autorizedDirectDebitMonthly].some(
        (item) => item?.display,
      ),
      optionlist: {
        autorizedDirectDebit: createOption(
          "offer.autorized.dd",
          autorizedDirectDebit,
        ),
        autorizedDirectDebitMonthly: createOption(
          "offer.autorized.monthly",
          autorizedDirectDebitMonthly,
        ),
      },
    },
    users: {
      title: getTitleDisplay({
        en: smart_access?.nameEn,
        fr: smart_access?.nameFr,
        id: "offer.users",
        intl,
      }),
      display: users?.display,
      optionlist: {
        users: createOption("offer.users", users),
      },
    },
  };
};
